import { memo, useMemo } from 'react';
import { useAtomValue } from 'jotai';
import { entAccountAvailablePointsAtom, entAccountDataAtom } from '@/stores/ent-account';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRightIcon,
  CircleWarningFilledIcon,
  RightIcon,
  TriangleWarningFilledIcon,
  UrgencyFilledIcon,
} from '@wisdesign/lsicon';
import { cn } from '@/lib/utils';
import PointCount from '@/pages/paid-management/componets/point-count';
import { Button } from '../ui/button';
import { Separator } from '../ui/separator';
import { getDaysLeft, getPointDaysLeft } from '@/utils/paid-management';
import { PermissionGuard } from '../permission-guard';
import { usePermissions } from '@/hooks/use-permissions';
import { Alert } from '@/components/ui/alert-dialog';
import { PLAN_PACKAGE_CONFIG } from '@/pages/paid-management/utils';
import { PlanPackageVersionCode } from '@/interfaces/paid-management';

type CombinedStatus =
  | 'normal' // 套餐和点数都正常
  | 'package-warning-point-normal' // 套餐提醒，点数正常
  | 'point-warning-package-normal' // 点数提醒，套餐正常
  | 'both-warning' // 点数和套餐都提醒
  | 'free'; // 免费版

const EntBalanceStatusIconMap: Record<string, React.ReactNode> = {
  trial: <CircleWarningFilledIcon className="text-error w-[16px] h-[16px]" />,
  free: null,
  normal: <UrgencyFilledIcon className="text-success w-[16px] h-[16px]" />,
  warn: <TriangleWarningFilledIcon className="text-warning w-[16px] h-[16px]" />,
  danger: <CircleWarningFilledIcon className="text-error w-[16px] h-[16px]" />,
};

const EntBalanceStatusBgMap: Record<string, string> = {
  trial: 'bg-[linear-gradient(180deg,rgba(243,38,64,0.05)_0%,rgba(243,38,64,0.20)_102.27%)]',
  free: 'bg-[linear-gradient(180deg,rgba(0,0,0,0.05)_0%,rgba(0,0,0,0.10)_100%)]',
  normal: 'bg-[#0794551A]',
  warn: 'bg-[linear-gradient(180deg,rgba(255,169,31,0.05)_0%,rgba(255,169,31,0.20)_102.27%)]',
  danger: 'bg-[linear-gradient(180deg,rgba(243,38,64,0.05)_0%,rgba(243,38,64,0.20)_102.27%)]',
};

const EntBalanceStatusBadgeColorMap: Record<string, string> = {
  trial: 'bg-[#F326401A]',
  free: 'bg-[#0794551A]',
  normal: 'bg-[#0794551A]',
  warn: 'bg-[#FF98001A]',
  danger: 'bg-[#F326401A]',
};

interface PointBannerProps {
  fold?: boolean;
}

const EntBalanceBanner = ({ fold = false }: PointBannerProps) => {
  const availablePoints = useAtomValue(entAccountAvailablePointsAtom);
  const entAccount = useAtomValue(entAccountDataAtom);
  const averageDailyConsumePoints = entAccount?.averageDailyConsumePoints ?? 0;
  const pointDaysLeft = getPointDaysLeft(availablePoints, averageDailyConsumePoints);
  const navigate = useNavigate();
  const { canViewPointManagement } = usePermissions();

  // 是否付费
  const isPaid = useMemo(() => {
    return entAccount?.hasPaid;
  }, [entAccount?.hasPaid]);

  // 判断是否在试用中
  const isTrial = useMemo(() => {
    return entAccount?.trialStatus === 'NORMAL';
  }, [entAccount?.trialStatus]);

  // 判断是否为套餐到期
  const isPackageExpired = useMemo(() => {
    return entAccount?.packageStatus === 'EXPIRED';
  }, [entAccount?.packageStatus]);

  const packageDayLefts = getDaysLeft(
    isTrial ? entAccount?.packageTrialExpiredAt : entAccount?.packageExpiredAt,
  );

  // 判断点数状态级别：>30天正常，7-30天警告，<=7天危险，已用完
  const pointStatusLevel = useMemo(() => {
    if (availablePoints <= 0) return 'used-up';
    if (pointDaysLeft <= 7) return 'danger';
    if (pointDaysLeft <= 30) return 'warn';
    return 'health';
  }, [pointDaysLeft, availablePoints]);

  // 判断套餐状态级别：>30天正常，7-30天警告，<=7天或已到期危险
  const packageStatusLevel = useMemo(() => {
    if (isPackageExpired) return 'danger';
    if (packageDayLefts <= 7) return 'danger';
    if (packageDayLefts <= 30) return 'warn';
    return 'health';
  }, [isPackageExpired, packageDayLefts]);

  // 判断是否为点数充足
  const isPointSufficient = useMemo(() => {
    return pointStatusLevel === 'health';
  }, [pointStatusLevel]);

  // 判断是否为套餐充足
  const isPackageSufficient = useMemo(() => {
    return packageStatusLevel === 'health';
  }, [packageStatusLevel]);

  // 计算组合状态 - 用于判断显示什么内容
  const combinedStatus: CombinedStatus = useMemo(() => {
    // 未付费且没有在试用中 - 免费版
    if (!isPaid && !isTrial) return 'free';

    // 套餐和点数都正常
    if (isPointSufficient && isPackageSufficient) return 'normal';

    // 套餐提醒，点数正常
    if (isPointSufficient && !isPackageSufficient) return 'package-warning-point-normal';

    // 点数提醒，套餐正常
    if (!isPointSufficient && isPackageSufficient) return 'point-warning-package-normal';

    // 点数和套餐都提醒
    return 'both-warning';
  }, [isPaid, isTrial, isPointSufficient, isPackageSufficient]);

  // 计算按钮文本
  const buttonText = useMemo(() => {
    // 没有付费（免费版 或 试用中） → 「立即升级」
    if (!isPaid) return '立即升级';

    // 只有点数提醒 → 「购买点数」
    if (!isPointSufficient && isPackageSufficient) return '购买点数';

    // 点数消耗完毕，套餐不足 → 「购买点数」
    if (pointStatusLevel === 'used-up' && !isPackageSufficient && !isPackageExpired) {
      return '购买点数';
    }

    // 其他所有情况（有套餐提醒或到期）→ 「立即续费」
    return '立即续费';
  }, [isPaid, isPointSufficient, isPackageSufficient, pointStatusLevel, isPackageExpired]);

  const displayStatus = useMemo(() => {
    // 试用和免费优先
    if (!isPaid && !isTrial) return 'free';

    if (isTrial) return 'trial';

    // 都正常
    if (isPointSufficient && isPackageSufficient) return 'normal';

    // 判断是否有任何一项是危险状态
    if (
      packageStatusLevel === 'danger' ||
      pointStatusLevel === 'danger' ||
      pointStatusLevel === 'used-up'
    ) {
      return 'danger';
    }

    // 判断是否有任何一项是警告状态
    if (packageStatusLevel === 'warn' || pointStatusLevel === 'warn') {
      return 'warn';
    }

    return 'normal';
  }, [
    isPaid,
    isTrial,
    isPointSufficient,
    isPackageSufficient,
    packageStatusLevel,
    pointStatusLevel,
  ]);

  const handleClick = () => {
    if (!canViewPointManagement()) {
      Alert.confirm({
        title: '抱歉，您暂无权限进入该页面',
        description: '',
        hideCancel: true,
      });
      return;
    }
    navigate('/paid-management');
  };

  const handleButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (!canViewPointManagement()) {
      Alert.confirm({
        title: '抱歉，您暂无权限进入该页面',
        description: '',
        hideCancel: true,
      });
      return;
    }
    if (!isPaid) {
      // 没有付费：跳转到开通页面
      navigate('/paid-management/open');
    } else if (buttonText === '购买点数') {
      // 购买点数：跳转到点数购买页面
      navigate('/paid-management/point');
    } else if (buttonText === '立即续费') {
      // 立即续费：跳转到续费页面
      navigate('/paid-management/renew');
    } else {
      // 默认跳转到付费管理页面
      navigate('/paid-management');
    }
  };

  // 获取套餐版本名称
  const packageVersionName = useMemo(() => {
    if (!entAccount?.packagePlan) return '';
    const config = PLAN_PACKAGE_CONFIG[entAccount.packagePlan as PlanPackageVersionCode];
    return config?.name || '';
  }, [entAccount?.packagePlan]);

  // 获取状态显示信息
  const statusDisplay = useMemo(() => {
    return {
      badgeIcon: EntBalanceStatusIconMap[displayStatus],
      bgColor: EntBalanceStatusBgMap[displayStatus],
      badgeColor: EntBalanceStatusBadgeColorMap[displayStatus],
    };
  }, [displayStatus]);

  // Fold 状态的渲染方法
  const renderFoldContainer = (children: React.ReactNode) => (
    <div
      className="rounded p-xs bg-base w-[55px] cursor-pointer hover:shadow-md"
      onClick={handleClick}
    >
      <div className={`flex flex-col items-center rounded ${statusDisplay.bgColor} p-xs`}>
        {children}
      </div>
    </div>
  );

  const renderFoldNormal = () =>
    renderFoldContainer(
      <>
        <UrgencyFilledIcon className="text-success w-[16px] h-[16px] mb-sm" />
        <div className="text-sm text-03">健康</div>
      </>,
    );

  const renderFoldPackageInfo = () =>
    renderFoldContainer(
      <>
        <div className="text-xs text-02 mb-sm">{`套餐${isPackageExpired ? '' : '剩余'}`}</div>
        <div className="text-sm font-medium text-01 mb-xs">
          {isPackageExpired ? '已到期' : `${Math.floor(packageDayLefts)}天`}
        </div>
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            'mt-xs h-[28px] w-full',
            displayStatus === 'warn'
              ? 'bg-background text-01 hover:bg-background hover:text-01'
              : 'bg-primary text-invert hover:bg-primary hover:text-invert',
          )}
          onClick={handleButtonClick}
        >
          {isPaid ? '续费' : '升级'}
        </Button>
      </>,
    );

  const renderFoldPointInfo = () =>
    renderFoldContainer(
      <>
        <div className="text-xs text-02 mb-sm">点数剩余</div>
        <PointCount
          count={availablePoints}
          needAdjust={true}
          className="flex-col p-sm w-[40px] rounded-[6px] bg-base-alpha-70"
        />
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            'mt-xs h-[28px] w-full',
            displayStatus === 'warn'
              ? 'bg-base text-01 hover:bg-base'
              : 'bg-primary text-invert hover:bg-primary',
          )}
          onClick={handleButtonClick}
        >
          {isPaid ? '充值' : '升级'}
        </Button>
      </>,
    );

  const renderFoldContent = () => {
    // 正常状态
    if (combinedStatus === 'normal') {
      return renderFoldNormal();
    }

    // 套餐异常，点数正常 - 显示套餐信息
    if (combinedStatus === 'package-warning-point-normal') {
      return renderFoldPackageInfo();
    }

    // 点数异常，套餐正常 - 显示点数信息
    if (combinedStatus === 'point-warning-package-normal') {
      return renderFoldPointInfo();
    }

    // 两者都异常 - 如果点数已用完，显示点数信息；否则显示套餐信息
    if (combinedStatus === 'both-warning') {
      // 点数已用完，显示点数信息让用户充值
      if (pointStatusLevel === 'used-up' || availablePoints <= 0) {
        return renderFoldPointInfo();
      }
      // 点数还有，显示套餐信息
      return renderFoldPackageInfo();
    }

    // 免费版 - 显示点数信息
    return renderFoldPointInfo();
  };

  const renderPointManage = () => {
    const getBadgeText = () => {
      if (isTrial) return '试用中';
      if (!isPaid) return '免费版';
      if (isPackageExpired && packageVersionName) {
        return `${packageVersionName}-到期`;
      }
      if (availablePoints < 0) return '已欠费';
      // 只有点数提醒 → 余额预警
      if (combinedStatus === 'point-warning-package-normal') {
        return pointDaysLeft > 0 ? '点数预警' : '点数不足';
      }
      // 其他情况 → 即将到期
      return '即将到期';
    };

    return (
      <div className="px-lg py-md flex justify-between items-center w-full">
        <div>
          <div className="text-02 text-sm font-medium mb-sm">付费管理</div>
          <div
            className={`flex gap-sm items-center justify-center ${statusDisplay.badgeColor} px-[6px] py-xs rounded-full inline-block`}
          >
            {statusDisplay.badgeIcon}
            <div className="text-sm text-03">{getBadgeText()}</div>
          </div>
        </div>
        <RightIcon className="w-[16px] h-[16px]" />
      </div>
    );
  };

  // 渲染正常状态（套餐和点数都正常）
  const renderNormalStatus = () => {
    return (
      <div className="px-lg py-md flex justify-between items-center w-full">
        <div>
          <div className="text-02 text-sm font-medium mb-sm">点数管理</div>
          <div
            className={`flex gap-sm items-center justify-center ${statusDisplay.badgeColor} px-[6px] py-xs rounded-full inline-block`}
          >
            <div className="text-sm text-03">余额正常</div>
          </div>
        </div>
        <RightIcon className="w-[16px] h-[16px]" />
      </div>
    );
  };

  // 渲染套餐即将到期、点数正常
  const renderPackageWarningPointNormal = () => {
    return (
      <div className={cn('rounded flex flex-col items-center w-full', statusDisplay.bgColor)}>
        <div className="w-full p-md">
          <div className="p-md w-full flex flex-col items-center gap-sm bg-[rgba(255,255,255,0.7)] rounded-[6px] backdrop-filter-[1px] overflow-hidden">
            <div className="text-03 text-xs">
              {isPackageExpired ? '套餐已到期' : '套餐剩余有效期'}
            </div>
            {!isPackageExpired && (
              <div className="text-xs text-helper">
                <span className="text-01 font-medium text-base">{Math.floor(packageDayLefts)}</span>
                {`天`}
              </div>
            )}
          </div>
          <PermissionGuard permission="pointManagement:viewPage" type="show">
            <Button
              variant="ghost"
              size="sm"
              className={cn(
                'mt-md w-full h-[32px]',
                displayStatus === 'warn'
                  ? 'bg-background text-01 hover:bg-background hover:text-01'
                  : 'bg-primary text-invert hover:bg-primary hover:text-invert',
              )}
              onClick={handleButtonClick}
            >
              {buttonText}
              <ArrowRightIcon className="ml-2" />
            </Button>
          </PermissionGuard>
        </div>

        <Separator className="bg-black/10 mt-md" />
        <div className="flex justify-center w-full">{renderPointManage()}</div>
      </div>
    );
  };

  // 渲染点数即将用完、套餐正常
  const renderPointWarningPackageNormal = () => {
    return (
      <div
        className={cn('rounded flex flex-col items-center w-full p-[1px]', statusDisplay.bgColor)}
      >
        <div className="flex gap w-full p-md justify-between">
          <div className="p-md bg-[rgba(255,255,255,0.7)] rounded-[6px] flex flex-col items-center gap-sm flex-1 backdrop-filter-[1px] overflow-hidden">
            <div className="text-03 text-xs">点数余额</div>
            <PointCount
              count={availablePoints}
              needAdjust={true}
              className="bg-transparent gap-sm"
              iconClassName="w-[20px] h-[20px]"
            />
          </div>
          {pointDaysLeft > 0 && pointDaysLeft !== Infinity && (
            <div className="p-md bg-[rgba(255,255,255,0.7)] rounded-[6px] flex flex-col items-center gap-sm flex-1 backdrop-filter-[1px] overflow-hidden">
              <div className="text-03 text-xs">还能消耗</div>
              <div className="text-xs text-helper flex items-center justify-center">
                <span className="text font-medium mr-xs">{Math.floor(pointDaysLeft)}</span>天
              </div>
            </div>
          )}
        </div>
        <PermissionGuard permission="pointManagement:viewPage" type="show">
          <Button
            variant="ghost"
            size="sm"
            className={cn(
              'mt-xs h-[28px] mx-md w-[calc(100%-16px)]',
              displayStatus === 'warn'
                ? 'bg-base text-01 hover:bg-base/80 hover:text-01'
                : 'bg-primary text-white hover:bg-primary/80 hover:text-white',
            )}
            onClick={handleButtonClick}
          >
            {buttonText}
            <ArrowRightIcon className="ml-2" />
          </Button>
        </PermissionGuard>
        <Separator className="bg-black/10 mt-md" />
        <div className="flex justify-center w-full">{renderPointManage()}</div>
      </div>
    );
  };

  // 渲染点数和套餐都即将到期
  const renderBothWarning = () => {
    // 套餐已到期时不显示点数余额
    if (isPackageExpired) {
      return (
        <div
          className={cn('rounded flex flex-col items-center w-full p-[1px]', statusDisplay.bgColor)}
        >
          <div className="p-md w-full flex flex-col items-center gap-sm">
            <div className="text-03 text-xs">套餐剩余有效期</div>
            <div className="text-lg font-medium text-01">套餐已到期</div>
          </div>
          <PermissionGuard permission="pointManagement:viewPage" type="show">
            <Button
              variant="ghost"
              size="sm"
              className="mt-xs h-[28px] mx-md w-[calc(100%-16px)] bg-primary text-white hover:bg-primary/80 hover:text-white"
              onClick={handleButtonClick}
            >
              {buttonText}
              <ArrowRightIcon className="ml-2" />
            </Button>
          </PermissionGuard>
          <Separator className="bg-black/10 mt-md" />
          <div className="flex justify-center w-full">{renderPointManage()}</div>
        </div>
      );
    }

    // 套餐未到期，显示点数和套餐信息
    return (
      <div
        className={cn('rounded flex flex-col items-center w-full p-[1px]', statusDisplay.bgColor)}
      >
        <div className="flex gap w-full p-md">
          <div className="py-md bg-[rgba(255,255,255,0.7)] rounded-[6px] flex flex-col items-center justify-center gap-sm flex-1 backdrop-filter-[1px] overflow-hidden">
            <div className="text-03 text-xs">点数余额</div>
            <PointCount
              count={availablePoints}
              needAdjust={true}
              className="bg-transparent gap-sm"
              iconClassName="w-[20px] h-[20px]"
            />
          </div>
          <div className="py-md bg-[rgba(255,255,255,0.7)] rounded-[6px] flex flex-col items-center justify-center gap-sm flex-1 backdrop-filter-[1px] overflow-hidden">
            <div className="text-03 text-xs">{isTrial ? '试用剩余' : '套餐剩余'}</div>
            <div className="text-xs text-helper flex items-center justify-center">
              <span className="text font-medium mr-xs">{Math.floor(packageDayLefts)}</span>天
            </div>
          </div>
        </div>
        <PermissionGuard permission="pointManagement:viewPage" type="show">
          <Button
            variant="ghost"
            size="sm"
            className={cn(
              'mt-xs h-[28px] mx-md w-[calc(100%-16px)]',
              displayStatus === 'warn'
                ? 'bg-base text-01 hover:bg-base/80 hover:text-01'
                : 'bg-primary text-white hover:bg-primary/80 hover:text-white',
            )}
            onClick={handleButtonClick}
          >
            {buttonText}
            <ArrowRightIcon className="ml-2" />
          </Button>
        </PermissionGuard>
        <Separator className="bg-black/10 mt-md" />
        <div className="flex justify-center w-full">{renderPointManage()}</div>
      </div>
    );
  };

  // 渲染免费版
  const renderFreeStatus = () => {
    return (
      <div className="w-full space-y-md">
        <div
          className={cn(
            'flex gap w-full p-md',
            pointDaysLeft > 0 ? 'justify-between' : 'justify-center',
          )}
        >
          <div
            className={cn(
              'p-md rounded-[6px] flex flex-col items-center gap-sm flex-1',
              pointDaysLeft <= 0 && 'w-full max-w-[200px]',
              'bg-[linear-gradient(180deg,rgba(0,0,0,0.04)_0%,rgba(102,102,102,0.02)_100%)] backdrop-blur-[1px]',
            )}
          >
            <div className="text-03 text-xs">剩余点数</div>
            <PointCount
              count={availablePoints}
              needAdjust={true}
              className="bg-transparent gap-sm"
              iconClassName="w-[20px] h-[20px]"
            />
          </div>
        </div>
        <PermissionGuard permission="pointManagement:viewPage" type="show">
          <Button
            variant="ghost"
            size="sm"
            className="mt-xs h-[28px] mx-md w-[calc(100%-16px)] bg-primary text-white hover:bg-primary/80 hover:text-white"
            onClick={handleButtonClick}
          >
            {buttonText}
            <ArrowRightIcon className="ml-2" />
          </Button>
        </PermissionGuard>
        <Separator className="bg-black/10 mt-md" />
        <div className="flex justify-center w-full">{renderPointManage()}</div>
      </div>
    );
  };

  // 状态到渲染函数的映射
  const statusRenderMap: Partial<Record<CombinedStatus, () => React.ReactNode>> = {
    normal: renderNormalStatus,
    'package-warning-point-normal': renderPackageWarningPointNormal,
    'point-warning-package-normal': renderPointWarningPackageNormal,
    'both-warning': renderBothWarning,
    free: renderFreeStatus,
  };

  // 获取当前状态的渲染函数
  const renderContent = () => {
    if (fold) {
      return renderFoldContent();
    }
    const renderFn = statusRenderMap[combinedStatus];
    return renderFn ? renderFn() : renderFreeStatus();
  };

  return (
    <div
      className={cn(
        'bg-base rounded cursor-pointer hover:shadow-md',
        combinedStatus !== 'normal' && 'flex flex-col items-center p-[1px]',
      )}
      onClick={handleClick}
    >
      {renderContent()}
    </div>
  );
};

export default memo(EntBalanceBanner);
